import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, productImage } from "../lib/brand";
import { useCart } from "../lib/cart";
import BrandMark from "../components/BrandMark";
import { CheckCircle2, Clock, XCircle, ArrowLeft, Download } from "lucide-react";
import { toast } from "sonner";

export default function Success() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [state, setState] = useState({ status: "checking", payment_status: "pending", products: [] });
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", payment_status: "missing_session" });
      return;
    }
    let attempts = 0;
    const maxAttempts = 8;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/checkout/status/${sessionId}`);
        setState(res.data);
        if (res.data.payment_status === "paid") {
          if (!cleared.current) {
            clear();
            cleared.current = true;
          }
          return;
        }
        if (res.data.status === "expired") return;
        if (attempts < maxAttempts) {
          attempts += 1;
          setTimeout(poll, 2000);
        }
      } catch (e) {
        if (attempts < maxAttempts) {
          attempts += 1;
          setTimeout(poll, 2000);
        } else {
          setState({ status: "error", payment_status: "error" });
        }
      }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const downloadFile = async (product) => {
    try {
      const res = await axios.get(`${API}/downloads/${sessionId}/${product.id}`, {
        params: { email: state.email },
        responseType: "blob",
      });
      if (res.headers["content-type"]?.includes("application/json")) {
        const text = await res.data.text();
        const json = JSON.parse(text);
        if (json.redirect_url) { window.open(json.redirect_url, "_blank"); return; }
      }
      const blob = new Blob([res.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const ext = product.audio_path?.split(".").pop() || product.cover_path?.split(".").pop() || "jpg";
      a.download = `${product.title}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Download started");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Download failed");
    }
  };

  const isPaid = state.payment_status === "paid";
  const isExpired = state.status === "expired";
  const isError = state.status === "error";

  return (
    <div data-testid="success-page" className="pt-32 pb-24 min-h-screen">
      <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
        <BrandMark variant="knot" className="h-16 w-16 mx-auto mb-8 block" />

        {isPaid ? (
          <>
            <CheckCircle2 className="w-16 h-16 text-[#279153] mx-auto mb-6" />
            <div className="text-[11px] tracking-[0.4em] uppercase text-[#279153] mb-3">Go raibh maith agat</div>
            <h1 data-testid="success-title" className="font-serif-display text-5xl text-white mb-6">Thank you</h1>
            <p className="text-base text-[#95A89C] mb-2">
              Your payment was received. A thank-you note is on its way to{" "}
              <span className="text-white">{state.email}</span>.
            </p>
            <p className="text-sm text-[#5C7064] mb-10">
              Total: <span className="text-white font-serif-display">${Number(state.amount || 0).toFixed(2)} USD</span>
            </p>

            {state.products?.length > 0 && (
              <div data-testid="success-downloads" className="mt-2 mb-12 text-left">
                <div className="text-[11px] tracking-[0.4em] uppercase text-[#5C7064] mb-4 text-center">Your files</div>
                <div className="space-y-3">
                  {state.products.map((p) => (
                    <div key={p.id} className="flex items-center gap-4 p-4 bg-[#09120C] border border-[#1A2B20]">
                      <div className="w-14 h-14 bg-black flex-shrink-0 overflow-hidden">
                        <img src={productImage(p)} alt="" className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0 text-left">
                        <div className="text-[10px] tracking-[0.3em] uppercase text-[#5C7064]">{p.type}</div>
                        <div className="font-serif-display text-lg text-white truncate">{p.title}</div>
                      </div>
                      <button
                        data-testid={`success-download-${p.id}`}
                        onClick={() => downloadFile(p)}
                        className="flex items-center gap-2 px-4 py-2 border border-[#279153] text-[#279153] text-xs tracking-[0.3em] uppercase hover:bg-[#279153] hover:text-white"
                      >
                        <Download className="w-4 h-4" /> Download
                      </button>
                    </div>
                  ))}
                </div>
                <p className="mt-4 text-xs text-[#5C7064] text-center">
                  These links remain accessible via{" "}
                  <Link to={`/orders?session=${sessionId}&email=${state.email}`} className="text-[#279153] hover:underline">
                    your orders page
                  </Link>.
                </p>
              </div>
            )}
          </>
        ) : isExpired || isError ? (
          <>
            <XCircle className="w-16 h-16 text-red-400 mx-auto mb-6" />
            <h1 className="font-serif-display text-5xl text-white mb-6">Something went amiss</h1>
            <p className="text-base text-[#95A89C] mb-10">
              Your session expired or could not be confirmed. Please try again — no charge was made.
            </p>
          </>
        ) : (
          <>
            <Clock className="w-16 h-16 text-[#95A89C] mx-auto mb-6 animate-pulse" />
            <h1 className="font-serif-display text-5xl text-white mb-6">Confirming…</h1>
            <p className="text-base text-[#95A89C] mb-10">Just a moment while we confirm your payment.</p>
          </>
        )}

        <Link
          to="/"
          data-testid="success-home-link"
          className="inline-flex items-center gap-3 px-7 py-4 border border-white/30 text-white text-xs tracking-[0.3em] uppercase hover:border-[#279153] hover:text-[#279153]"
        >
          <ArrowLeft className="w-4 h-4" /> Back to studio
        </Link>
      </div>
    </div>
  );
}
